import { useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Dialog, DialogContent, DialogDescription, DialogFooter, 
  DialogHeader, DialogTitle, DialogTrigger 
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ScrollableTable, Column, SortConfig } from "@/components/ui/scrollable-table";
import { Pencil, Trash2 } from "lucide-react";

import { useCollection } from "@/hooks/firestore";
import { 
  useAddDocument, 
  useUpdateDocument, 
  // useDeleteDocument // No longer used directly
} from "@/hooks/firestore/operations";
import { usePermissions } from "@/hooks/usePermissions";
import { Flavor } from "@/types/firestore";

// Redux imports for deletion dialog
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { DeleteDialog } from "../editor/dialogs/DeleteConfirmDialog";
import { 
    EditableEntityType,
    startDeletionAssessment, 
    assessDeletionImpact, 
    deleteEntity, 
    cancelInteraction,
    selectEditorInteractionState,
    selectSelectedEntity,
    selectDeletionImpact,
    selectEditorOperationLoading,
    selectEditorOperationError
} from "@/store/slices/editorSlice"; 

export const FlavorManager = () => {
  const dispatch = useAppDispatch();
  const { 
    documents: flavors, 
    loading: collectionLoading 
  } = useCollection<Flavor>('flavors'); // Removed default sort, will handle in useMemo
  
  // Instantiate individual operation hooks
  const { addDocument, loading: adding, error: addError } = useAddDocument<Flavor>('flavors');
  const { updateDocument, loading: updating, error: updateError } = useUpdateDocument<Flavor>('flavors');
  // const { deleteDocument, loading: deleting, error: deleteError } = useDeleteDocument('flavors'); // Replaced

  // Editor slice selectors
  const interactionState = useAppSelector(selectEditorInteractionState);
  const selectedEntity = useAppSelector(selectSelectedEntity);
  const deletionImpact = useAppSelector(selectDeletionImpact);
  const operationLoading = useAppSelector(selectEditorOperationLoading);
  const operationError = useAppSelector(selectEditorOperationError);
  
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('manage_flavors') || hasPermission('admin');
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingFlavor, setEditingFlavor] = useState<Flavor | null>(null);
  const [newFlavor, setNewFlavor] = useState({ name: "", groupId: 1, orderInGroup: 1 });
  const [filterGroup, setFilterGroup] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState<string | null>('groupId');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Calculate flavor groups from the flavors collection
  const flavorsByGroup = useMemo(() => {
    if (!flavors) return {};
    return flavors.reduce<Record<number, Flavor[]>>((groups, flavor) => {
      const groupId = flavor.groupId || 0;
      if (!groups[groupId]) {
        groups[groupId] = [];
      }
      groups[groupId].push(flavor);
      // Default sort within groups by orderInGroup - can be removed if main sort handles it
      // groups[groupId].sort((a, b) => (a.orderInGroup || 0) - (b.orderInGroup || 0));
      return groups;
    }, {});
  }, [flavors]);

  // Get unique group IDs
  const groupIds = useMemo(() => {
    return Object.keys(flavorsByGroup).map(Number).sort((a, b) => a - b);
  }, [flavorsByGroup]);

  // Filter flavors - sorting is now handled by ScrollableTable
  const processedFlavors = useMemo(() => {
    if (!flavors) return [];
    let filtered = flavors;

    // Filter by group
    if (filterGroup !== null) {
      filtered = filtered.filter((f: Flavor) => (f.groupId || 0) === filterGroup);
    }

    // Filter by search term (name)
    if (searchTerm) {
      filtered = filtered.filter((f: Flavor) => 
        f.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [flavors, filterGroup, searchTerm]);

  const handleSort = (column: keyof Flavor) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleAddFlavor = useCallback(async (): Promise<void> => {
    if (!newFlavor.name.trim()) {
      toast.error("Flavor name cannot be empty.");
      return;
    }
    if (isNaN(newFlavor.groupId)) {
       toast.error("Group ID must be a valid number.");
       return;
    }
    if (isNaN(newFlavor.orderInGroup)) {
       toast.error("Order must be a valid number.");
       return;
    }

    try {
        const flavorToAdd: Omit<Flavor, 'id' | 'createdAt'> = {
            name: newFlavor.name.trim(),
            groupId: newFlavor.groupId,
            orderInGroup: newFlavor.orderInGroup
        };
        const newId = await addDocument(flavorToAdd);
        if (newId) {
          setNewFlavor({ name: "", groupId: 1, orderInGroup: 1 });
          setIsAddDialogOpen(false);
          toast.success(`Flavor "${newFlavor.name}" added.`);
        } else {
          toast.error(`Failed to add flavor: ${addError?.message || 'Unknown error'}`);
        }
    } catch (error) {
        console.error("Error adding flavor:", error);
        toast.error("Failed to add flavor. See console for details.");
    }
  }, [addDocument, newFlavor, addError]);

  const handleSaveEdit = useCallback(async (updatedFlavor: Flavor): Promise<void> => {
    if (!updatedFlavor.id) {
        toast.error("Cannot update flavor: Missing ID.");
        setEditingFlavor(null);
        return;
    }
    if (!updatedFlavor.name.trim()) {
      toast.error("Flavor name cannot be empty.");
      return;
    }
    if (isNaN(updatedFlavor.groupId)) {
       toast.error("Group ID must be a valid number.");
       return;
    }
    if (isNaN(updatedFlavor.orderInGroup)) {
       toast.error("Order must be a valid number.");
       return;
    }

    try {
        const success = await updateDocument(updatedFlavor.id, {
            name: updatedFlavor.name.trim(),
            groupId: updatedFlavor.groupId,
            orderInGroup: updatedFlavor.orderInGroup
        });
        if (success) {
          setEditingFlavor(null);
          toast.success(`Flavor "${updatedFlavor.name}" updated.`);
        } else {
           toast.error(`Failed to update flavor: ${updateError?.message || 'Unknown error'}`);
        }
    } catch(error) {
        console.error("Error updating flavor:", error);
        toast.error("Failed to update flavor. See console for details.");
    }
  }, [updateDocument, updateError]);

  const handleCancelEdit = useCallback(() => {
    setEditingFlavor(null);
  }, []);

  // Updated delete flavor handler to use Redux dialog flow
  const handleDeleteFlavor = useCallback((flavor: Flavor) => {
    if (!flavor || !flavor.id) return;
    dispatch(startDeletionAssessment({ entityType: 'flavor', entityId: flavor.id }));
    // Flavors require impact assessment to show which drinks will be affected
    dispatch(assessDeletionImpact({ entityType: 'flavor', entityId: flavor.id }));
  }, [dispatch]);

  // Dialog handlers
  const handleDeletionDialogClose = useCallback(() => {
    dispatch(cancelInteraction());
  }, [dispatch]);

  const handleConfirmFlavorDeletion = useCallback(async () => {
    if (selectedEntity?.type === 'flavor' && selectedEntity.id) {
      try {
        await dispatch(deleteEntity({ entityType: 'flavor', entityId: selectedEntity.id })).unwrap();
        
        if (editingFlavor?.id === selectedEntity.id) {
          setEditingFlavor(null);
        }
      } catch (error: any) {
        // Error handling is now done by the dialog component
        throw error;
      }
    }
  }, [dispatch, selectedEntity, editingFlavor]);

  // Helper function to get deletion assessment state
  const getDeletionAssessmentState = useCallback(() => {
    if (deletionImpact.loading === 'pending') return 'loading';
    if (deletionImpact.loading === 'failed') return 'error';
    return 'completed';
  }, [deletionImpact.loading]);

  const handleUpdateNewFlavor = useCallback((field: keyof Omit<Flavor, 'id'>, value: string) => {
     setNewFlavor(prev => {
        const isNumericField = field === 'orderInGroup' || field === 'groupId';
        const processedValue = isNumericField ? parseInt(value) : value;
         return {...prev, [field]: processedValue };
     });
  }, []);

  const loading = collectionLoading || adding || updating || (operationLoading === 'pending' && interactionState === 'confirming_deletion');
  
  // Create table columns configuration
  const tableColumns = useMemo(() => {
    const columns: Column<Flavor>[] = [];
    
    columns.push({
      id: 'groupId',
      header: 'Group',
      cell: (flavor) => editingFlavor?.id === flavor.id ? (
        <Input
          type="number"
          value={editingFlavor.groupId}
          onChange={(e) => setEditingFlavor({...editingFlavor, groupId: parseInt(e.target.value)})}
          disabled={!canManage}
        />
      ) : flavor.groupId,
      sortable: true,
      minWidth: '100px'
    });

    columns.push({
      id: 'orderInGroup',
      header: 'Order',
      cell: (flavor) => editingFlavor?.id === flavor.id ? (
        <Input
          type="number"
          value={editingFlavor.orderInGroup}
          onChange={(e) => setEditingFlavor({...editingFlavor, orderInGroup: parseInt(e.target.value)})}
          disabled={!canManage}
          min="1"
        />
      ) : flavor.orderInGroup,
      sortable: true,
      minWidth: '100px'
    });

    columns.push({
      id: 'name',
      header: 'Name',
      cell: (flavor) => editingFlavor?.id === flavor.id ? (
        <Input
          value={editingFlavor.name}
          onChange={(e) => setEditingFlavor({...editingFlavor, name: e.target.value})}
          disabled={!canManage}
          maxLength={50}
        />
      ) : flavor.name,
      sortable: true,
      minWidth: '200px'
    });

    if (canManage) {
      columns.push({
        id: 'actions',
        header: 'Actions',
        cell: (flavor) => editingFlavor?.id === flavor.id ? (
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => handleSaveEdit(editingFlavor)} disabled={!canManage}>Save</Button>
            <Button variant="ghost" onClick={handleCancelEdit}>Cancel</Button>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditingFlavor(flavor)}
              title="Edit flavor"
              disabled={!flavor.id || !canManage}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => handleDeleteFlavor(flavor)}
              title="Delete flavor"
              disabled={!flavor.id || !canManage}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
        sortable: false,
        minWidth: '180px'
      });
    }
    
    return columns;
  }, [editingFlavor, canManage, handleSaveEdit, handleCancelEdit, handleDeleteFlavor]);
  
  // Create sort config for the table
  const tableSortConfig: SortConfig<Flavor> = useMemo(() => ({
    column: sortColumn as keyof Flavor | null,
    direction: sortDirection
  }), [sortColumn, sortDirection]);
  
  // Empty message with filters applied
  const emptyMessage = useMemo(() => {
    let message = "No flavors found";
    if (searchTerm) message += ` for "${searchTerm}"`;
    if (filterGroup !== null) message += ` for Group ${filterGroup}`;
    return message + '.';
  }, [searchTerm, filterGroup]);

  return (
    <>
    <Card className="h-full flex flex-col overflow-hidden">
      <CardHeader>
        <CardTitle>Soda Flavors ({flavors?.length || 0})</CardTitle>
        <CardDescription>Manage your soda flavors here</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col overflow-hidden pb-6">
        <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
          <div className="flex gap-2 items-end">
            <div>
              <Label htmlFor="groupFilter">Filter by Group</Label>
              <Select 
                value={filterGroup === null ? "all" : filterGroup.toString()} 
                onValueChange={(value) => {
                  setFilterGroup(value === "all" ? null : Number(value));
                }}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Groups</SelectItem>
                  {groupIds.map((group: number) => (
                    <SelectItem key={group} value={group.toString()}>
                      Group {group}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="searchTerm">Filter by Name</Label>
              <Input 
                id="searchTerm"
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-[200px]"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            {canManage && (
              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button>Add New</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Flavor</DialogTitle>
                    <DialogDescription>
                      Fill in the details for the new soda flavor.
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="name" className="text-right">Name</Label>
                      <Input 
                        id="name" 
                        value={newFlavor.name} 
                        onChange={(e) => handleUpdateNewFlavor('name', e.target.value)}
                        className="col-span-3"
                        maxLength={50}
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="groupId" className="text-right">Group</Label>
                      <Input 
                        id="groupId" 
                        type="number" 
                        value={newFlavor.groupId} 
                        onChange={(e) => handleUpdateNewFlavor('groupId', e.target.value)}
                        className="col-span-3"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="orderInGroup" className="text-right">Order</Label>
                      <Input 
                        id="orderInGroup" 
                        type="number" 
                        value={newFlavor.orderInGroup} 
                        onChange={(e) => handleUpdateNewFlavor('orderInGroup', e.target.value)}
                        className="col-span-3"
                        min="1"
                      />
                    </div>
                  </div>
                  
                  <DialogFooter>
                    <Button onClick={handleAddFlavor}>Add Flavor</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
        
        {loading ? (
          <div className="flex-1 flex items-center justify-center">Loading flavors...</div>
        ) : (
          <ScrollableTable
            data={processedFlavors}
            columns={tableColumns}
            sortConfig={tableSortConfig}
            onSort={handleSort}
            emptyMessage={emptyMessage}
            height="calc(100vh - 370px)"
          />
        )}
      </CardContent>
    </Card>

    {/* Deletion Dialog */}
    {(interactionState === 'confirming_deletion' || interactionState === 'assessing_deletion') && selectedEntity?.type === 'flavor' && (
      <DeleteDialog
        isOpen={true}
        onClose={handleDeletionDialogClose}
        entityType={selectedEntity.type as EditableEntityType}
        entityName={deletionImpact.report?.primaryDocument.name || selectedEntity.id || 'Flavor'}
        assessmentState={getDeletionAssessmentState()}
        impactReport={deletionImpact.report}
        deletionState={operationLoading === 'pending' ? 'loading' : operationError ? 'error' : 'idle'}
        error={deletionImpact.error || operationError}
        onConfirm={handleConfirmFlavorDeletion}
      />
    )}
  </>
  );
}; 